import isDate from 'lodash/isDate';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import type { StringUnitLength } from 'luxon';
import { DateTime, Info } from 'luxon';

import { diffInDays, getDateTime } from '../date';
import type { Nullable } from '../object';

import type { FormattingFn } from '.';

export enum DateFormat {
  yearMonth = 'yyyy-LL',
  yearMonthDay = 'yyyy-LL-dd',
  monthNameYear = 'LLLL yyyy',
  monthNameDayYear = 'LLLL dd yyyy',
  dayMonth = 'dd MMM',
  dayMonthYear = 'yyyy-LL-dd',
  monthYear = 'yyyy-LL',
  weekdayYearMonthDay = 'ccc, yyyy-LL-dd',
  weekdayAbbreviation = 'ccc',
  weekdayAbbreviationLong = 'cccC',
  month = 'LLLL',
  monthAbbreviation = 'MMM',
  csvDate = 'yyyy-LL-dd',
}

export enum TimeUnit {
  hour = 'hour',
  month = 'month',
  day = 'day',
  year = 'year',
}

const date: FormattingFn<Nullable<Date | DateTime | string>, string, DateFormat> = (
  { language },
) => (dateToFormat, dateFormat) => {
  if (isNil(dateToFormat)) {
    return '';
  }

  const base = (() => {
    if (isString(dateToFormat)) {
      return DateTime.fromISO(dateToFormat).setLocale(language).toFormat(dateFormat);
    }

    if (isDate(dateToFormat)) {
      return DateTime.fromJSDate(dateToFormat).setLocale(language).toFormat(dateFormat);
    }

    return dateToFormat.setLocale(language).toFormat(dateFormat);
  })();

  if (dateFormat === DateFormat.weekdayAbbreviation) {
    return base[0];
  }

  if (dateFormat === DateFormat.weekdayAbbreviationLong) {
    return base.slice(0, 2);
  }

  return base;
};

type RelativeDateArg = Readonly<{
  date: Date | DateTime;
  relativeTo: Date | DateTime;
  limit?: Readonly<{
    days: number;
    dateFormat: DateFormat;
  }>;
}>;

const relativeDate: FormattingFn<RelativeDateArg> = (
  config,
) => (
  arg,
) => {
  const diff = diffInDays(arg.date, arg.relativeTo);

  if (!isNil(arg.limit) && Math.abs(diff) > arg.limit.days) {
    return date(config)(arg.date, arg.limit.dateFormat);
  }

  return new Intl.RelativeTimeFormat(config.language, { numeric: 'auto' }).format(diff, 'day');
};

const hoursFN: FormattingFn<{ date: Date | string; relativeTo?: Date | string }> = (
  config,
) => (
  input,
) => {
  const relativeTo = DateTime.fromJSDate(new Date(input.relativeTo ?? new Date()));
  const baseDate = DateTime.fromJSDate(new Date(input.date));

  const formattedTime = baseDate.setLocale(config.language).toLocaleString(DateTime.TIME_SIMPLE);

  if (relativeTo.hasSame(baseDate, 'day')) {
    return formattedTime;
  }

  return `${date(config)(input.date, DateFormat.yearMonthDay)}, ${formattedTime}`;
};

const minutes: FormattingFn<number, string, StringUnitLength | 'clock'> = (
  { language, t },
) => (
  minutesToFormat, format,
) => {
  const hoursAmount = minutesToFormat < 0
    ? Math.ceil(minutesToFormat / 60) : Math.floor(minutesToFormat / 60);
  const minutesAmount = Math.abs(minutesToFormat % 60);

  if (format === 'clock') {
    return `${Math.abs(hoursAmount) < 10 ? `0${hoursAmount}` : hoursAmount} ${t('h')} ${minutesAmount < 10 ? `0${minutesAmount}` : minutesAmount} ${t('m')}`;
  }

  const unparsedHours = new Intl.NumberFormat(language, { style: 'unit', unit: 'hour', unitDisplay: format }).format(hoursAmount);

  const hours = unparsedHours.includes(hoursAmount.toString())
    ? unparsedHours.replace(new RegExp(`${hoursAmount}\\s?`), `${hoursAmount} `)
    : unparsedHours;

  if (minutesToFormat % 60 === 0) {
    return hours;
  }

  const unparsedMinutes = new Intl.NumberFormat(language, { style: 'unit', unit: 'minute', unitDisplay: format }).format(minutesAmount);

  const formattedMinutes = unparsedMinutes.includes(minutesAmount.toString())
    ? unparsedMinutes.replace(new RegExp(`${minutesAmount}\\s?`), `${minutesAmount} `)
    : unparsedMinutes;

  return `${hours} ${formattedMinutes}`;
};

type TimeUnitFormat = Readonly<{
  unit: TimeUnit;
  format?: StringUnitLength;
}>;

const timeUnit: FormattingFn<number, string, TimeUnitFormat> = (
  { language },
) => (daysToFormat, { unit, format }) => new Intl.NumberFormat(language, { style: 'unit', unit, unitDisplay: format ?? 'short' }).format(daysToFormat);

interface DurationArg {
  from: Date | DateTime;
  to: Date | DateTime;
}

// A whole-span duration as coarse years/months (e.g. "1 yr 7 mo", "3 mo"), falling back to
// days (min 1) for sub-month spans.
const duration: FormattingFn<DurationArg> = (config) => ({ from, to }) => {
  const formatUnit = timeUnit(config);
  const diff = getDateTime(to).diff(getDateTime(from), ['years', 'months', 'days']).toObject();
  const years = Math.floor(diff.years ?? 0);
  const months = Math.floor(diff.months ?? 0);

  if (years > 0 && months > 0) {
    return `${formatUnit(years, { unit: TimeUnit.year })} ${formatUnit(months, { unit: TimeUnit.month })}`;
  }
  if (years > 0) {
    return formatUnit(years, { unit: TimeUnit.year });
  }
  if (months > 0) {
    return formatUnit(months, { unit: TimeUnit.month });
  }
  return formatUnit(Math.max(1, Math.round(diff.days ?? 0)), { unit: TimeUnit.day });
};

interface DateRange {
  from: Nullable<Date | DateTime | string>;
  to?: Nullable<Date | DateTime | string>;
}
const dateRange: FormattingFn<DateRange, string, DateFormat> = (
  config,
) => (rangeToFormat, dateFormat) => {
  if (isNil(rangeToFormat.from)) {
    return '-';
  }

  const formattedStart = date(config)(rangeToFormat.from, dateFormat);

  if (isNil(rangeToFormat.to)) {
    return config.t('Since {{date}}', { date: formattedStart });
  }

  const formattedEnd = date(config)(rangeToFormat.to, dateFormat);

  if (formattedStart === formattedEnd) {
    return formattedStart;
  }

  return `${formattedStart} - ${formattedEnd}`;
};

const weekday: FormattingFn<Date | number, string, StringUnitLength> = (
  { language },
) => (day, format) => {
  const weekdayIndex = isDate(day) ? day.getDay() : day;

  const weekdays = Info.weekdays(format, { locale: language });
  return weekdays[(weekdayIndex + 6) % 7];
};

const clock: FormattingFn<number> = () => (time) => {
  const minutesPart = time % 60;
  const hoursPart = Math.floor(time / 60);

  return `${hoursPart < 10 ? `0${hoursPart}` : hoursPart}:${minutesPart < 10 ? `0${minutesPart}` : minutesPart}`;
};

const time = {
  date,
  relativeDate,
  weekday,
  dateRange,
  minutes,
  timeUnit,
  duration,
  clock,
  hours: hoursFN,
} as const;

export type Time = typeof time;

export default time;
